import { useEffect, useState, type JSX } from "react";
import { Outlet } from "react-router-dom";
import { setUpgradeModalFunction } from "@via/api/axios.tsx";
import Header from "@via/components/navigation/header.tsx";
import SideNavigation from "@via/components/navigation/side-navigation.tsx";
import UpgradeModal from "@via/pages/authentication/user-upgrade/upgrade-modal.tsx";
import CortexDrawer from "@via/components/cortex-drawer/cortex-drawer";
import { useAuth } from "@via/hooks/useAuth";
import { useUpgradeModal } from "@via/hooks/useUpgradeModal.tsx";
import { CortexProvider, useCortexContext } from "@via/context/cortex-context";
import { classNames } from "@via/utils/helpers.tsx";

function LayoutContent(): JSX.Element {
  const { isOpen, showUpgradeModal, hideUpgradeModal } = useUpgradeModal();
  const { user } = useAuth();
  const {
    isDrawerOpen,
    setIsDrawerOpen,
    selectedTokenId,
    setSelectedTokenId,
    shouldUsePortalForCortexDrawer,
    setActiveConversationId,
    setOpenTabIds,
  } = useCortexContext();
  const [isDrawerMinimized, setIsDrawerMinimized] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState<number | null>(null);

  useEffect(() => {
    setUpgradeModalFunction(showUpgradeModal);
  }, [showUpgradeModal]);

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedTokenId(null);
    setIsDrawerMinimized(false);
    setDrawerWidth(null);
    setActiveConversationId(null);
    setOpenTabIds(() => new Set());
  };

  const handleDrawerWidthChange = (width: number | null) => {
    setDrawerWidth(width);
  };

  const getDrawerWidthClass = () => {
    if (!user || !isDrawerOpen) return "w-0";
    if (isDrawerMinimized) return "w-16";
    if (drawerWidth !== null) return "";
    return "w-[28rem]";
  };

  const drawerWidthStyle =
    drawerWidth !== null && !isDrawerMinimized && isDrawerOpen
      ? { width: `${drawerWidth}px` }
      : undefined;

  return (
    <main className="via-main-wrapper">
      {user && <SideNavigation />}
      <div id="via-view-wrapper" className="via-view-wrapper">
        {user && <Header />}
        <Outlet />
      </div>
      {user && shouldUsePortalForCortexDrawer && isDrawerOpen && (
        <CortexDrawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          selectedTokenId={selectedTokenId}
          usePortal={true}
        />
      )}
      {user && !shouldUsePortalForCortexDrawer && (
        <div
          className={classNames(
            "sticky top-0 h-screen shrink-0 border-l border-gray-200 overflow-hidden transition-[width] duration-300 ease-in-out",
            getDrawerWidthClass()
          )}
          style={drawerWidthStyle}>
          {isDrawerOpen && (
            <CortexDrawer
              isOpen={isDrawerOpen}
              onClose={handleDrawerClose}
              selectedTokenId={selectedTokenId}
              usePortal={false}
              onMinimizedChange={setIsDrawerMinimized}
              onWidthChange={handleDrawerWidthChange}
            />
          )}
        </div>
      )}
      <UpgradeModal open={isOpen} setOpen={hideUpgradeModal} />
    </main>
  );
}

export default function MainLayout(): JSX.Element {
  return (
    <CortexProvider>
      <LayoutContent />
    </CortexProvider>
  );
}
